// Account - Join Promotion Page -  Auth Page
/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetServerSideProps } from 'next';
import { inspect } from 'util';
import { ApolloProvider } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';

import { ResponsiveGrid } from '@adobe/aem-react-editable-components';

import { respGridUtil, getProcessEnvs, prePageCall, Layout, logger } from '@marriott/mi-headless-utils';

import { getDeployedEnvType, useMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { FEATURE_FLAGS, TARGETS } from '@marriott/mi-feature-flag-utils';
import { constants } from '@marriott/mi-account-components/constants';

import { fetchFlags, preProcessorCall } from '@marriott/mi-account-components/utils';

import { JoinPromotionModel } from '../../../../_localDevMocks';
import operationSignatures from '../../../../_constants/operationSignatures.json';

const { NEXT_PUBLIC_AEM_PATH, SKIP_PREPROCESSOR_CALL, IS_LOCAL_DEV } = getProcessEnvs();
const { ITEM_PATH_ROOT, UNAUTH_URL, NEXT_PUBLIC_ENV_KEYS } = constants;

global.loggerInstance = logger({}) as CallableFunction;
const { log } = global.loggerInstance('JoinPromotion');

export default function JoinPromotion({ model, deployedEnvType }) {
  const MiApolloClient = useMiApolloClient(operationSignatures, deployedEnvType);

  const {
    metaNames,
    metaProperties,
    title,
    staticComponentClientLibsCSS,
    staticComponentClientLibsJS,
    gridCss,
    authorMode,
    canonical,
  } = model;
  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);

  return (
    <ApolloProvider client={MiApolloClient}>
      <Layout
        metaNames={metaNames}
        metaProps={metaProperties}
        title={title}
        staticComponentClientLibsCSS={staticComponentClientLibsCSS}
        staticComponentClientLibsJS={staticComponentClientLibsJS}
        gridCss={gridCss}
        isAuthorMode={authorMode}
        canonical={canonical}
      >
        <ResponsiveGrid
          {...responsiveGridModel}
          model={responsiveGridModel}
          pagePath={NEXT_PUBLIC_AEM_PATH}
          itemPath={ITEM_PATH_ROOT}
          customClassName="account-page-wrapper"
        />
      </Layout>
    </ApolloProvider>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const JoinPromotionSpecificData = async request => {
  const { EN_US, NON_EN_US } = constants;
  const deployedEnvType = getDeployedEnvType();
  let response: any = { operationSignatures: [], deployedEnvType };
  const resolvedUrlString: string = request?.req?.url;
  const isAuthorMode = resolvedUrlString?.includes('isAuthorMode');
  const queryString = new URLSearchParams(request?.query); // get query params
  const currentLocale =
    request?.req?.query?.locale ||
    (request?.req as any)?.locale ||
    request?.req?.headers?.['accept-language']?.split(',')?.[0]?.trim();

  const componentVariation =
    queryString.get('componentVariation') || request?.req?.headers?.['componentVariation'.toLowerCase()];
  const urlQueryParameters = queryString.toString();
  const preprocessorPath = urlQueryParameters ? `${UNAUTH_URL}?${urlQueryParameters}` : UNAUTH_URL;
  let featureFlags = await fetchFlags([FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENABLE_LEGAL_CONSENT], {
    target: TARGETS.APP.ACCOUNT_RENDERER,
  });

  if (componentVariation) {
    featureFlags[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS] = componentVariation;
    log.debug(`[JoinPromotionSpecificData] featureFlag (componentVariation) - ${inspect(featureFlags)}`);
  } else {
    let uniqueId = request?.req?.cookies?.enrollDeviceId;
    if (!uniqueId) {
      uniqueId = uuidv4();
      const expiresDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
      const expiresStr = expiresDate.toUTCString();
      const cookieString = `enrollDeviceId=${uniqueId}; path=/; HttpOnly; Secure; SameSite=None; Expires=${expiresStr};`;

      const prev = request?.res?.getHeader('Set-Cookie');
      const next = Array.isArray(prev)
        ? [...prev, cookieString]
        : prev
        ? [prev as string, cookieString]
        : [cookieString];

      request?.res?.setHeader('Set-Cookie', next);
    }

    const normalizedLocale = currentLocale ? currentLocale.replace('_', '-').toLowerCase() : EN_US;
    const isEnUsLocale = normalizedLocale === EN_US;
    const variationsFlags = await fetchFlags([FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS], {
      target: {
        ...TARGETS.APP.ACCOUNT_RENDERER,
        identifier: TARGETS.APP.ACCOUNT_RENDERER.identifier + `-${isEnUsLocale ? EN_US : NON_EN_US}`,
        name: uniqueId,
      },
    });
    featureFlags = { ...featureFlags, ...variationsFlags };
    log.debug(`[JoinPromotionSpecificData] UniqueId (id) - ${inspect(uniqueId)}`);
    if (featureFlags) {
      const ffValue = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS];
      log.debug(
        `[JoinPromotionSpecificData] FeatureFlags (fetchFlags) - experience_mcom_account_enrollment_variations: ${inspect(
          ffValue
        )}`
      );
    } else {
      log.warn('[JoinPromotionSpecificData] FeatureFlags (fetchFlags) returned empty/null');
    }
  }

  featureFlags = {
    ...featureFlags,
    ...(await fetchFlags([FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ACDL_ENABLED_PAGES_LOCALES], {
      target: TARGETS.APP.ACCOUNT_RENDERER,
    })),
  };

  /**
   * Preprocessor Call Start
   */

  /** Bypassing preprocessor call intentionally for testing. It should be reverted back */
  if (SKIP_PREPROCESSOR_CALL !== 'true' && IS_LOCAL_DEV !== 'true' && !isAuthorMode) {
    // Making page specific preprocessor call
    log.debug(`[JoinPromotionSpecificData] session Id: ${request?.req?.cookies?.sessionID}`);
    const preprocessorResponse = await preProcessorCall(
      request?.req?.cookies?.sessionID,
      request?.req,
      preprocessorPath,
      {}
    );
    log.debug(`[JoinPromotionSpecificData] PreprocessorResponse: ${inspect(preprocessorResponse)}`);
    if (preprocessorResponse?.data?.error) {
      const error = {
        redirect: { redirect: preprocessorResponse?.data?.redirect },
        value: true,
      };
      response = { ...response, error };
    } else if (preprocessorResponse?.data) {
      response = {
        operationSignatures: [],
        deployedEnvType,
        nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
      };
    }
  }
  /**
   * Preprocessor Call End
   */
  return { ...response, featureFlags: featureFlags ?? {} };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
export const getServerSideProps: GetServerSideProps<any> = prePageCall(JoinPromotionSpecificData, JoinPromotionModel);
